{#if isEnabled}
    <fieldset class="itsl-common-settings__fieldset | itsl-participants">
        <legend class="itsl-common-settings__fieldset-legend">
            {lang.main('Task_Assigned_to')}
        </legend>

        <div class="itsl-participants__selector">
            <div class="itsl-participants__selector-value">
                {valueText}
            </div>

            {#if isButtonVisible}
                {#if isLoading}
                    <LoadingSpinner label={lang.main('Loading')} visible size="small" />
                {:else}
                    <IconButton
                        large
                        htmlType="button"
                        type="plain"
                        iconClass="itsl-participants__add-remove--icon"
                        text={lang.main('Add_or_remove_participants')}
                        on:click={handleChangeButtonClick}
                    />
                {/if}
            {/if}
        </div>

        <ParticipantSelectorDialog
            {dialogId}
            bind:enabled={showModal}
            titleText={lang.main("Task_Assign_to")}
            {selectedParticipantIds}
            {selectedGroupIds}
            {pageSizesList}
            {selectedGroupName}
            groupsEnabled={participantSelectorSettings.groupsEnabled}
            getParticipantsFn={participantSelectorSettings.getParticipantsFn}
            getGroupsFn={participantSelectorSettings.getGroupsFn}
            addParticipantsLinkUrl={participantSelectorSettings.addParticipantsLinkUrl}
            addGroupLinkUrl={participantSelectorSettings.addGroupLinkUrl}
            on:cancel={handleCancel}
            on:dialogConfirmed={handleConfirmed}
        >
            <slot name="customBanner" slot="customBanner" />
        </ParticipantSelectorDialog>
    </fieldset>
{/if}

<script lang="ts">
    import { createEventDispatcher } from "svelte";

    import { guidV4 } from '@itslearning/atlas/generate/guid';
    import IconButton from "@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte";
    import LoadingSpinner from "@itslearning/prometheus/assets/nodes/LoadingSpinner/v1/LoadingSpinner.svelte";

    import ParticipantSelectorDialog from "./dialog/ParticipantSelectorDialog.svelte";
    import {
        type AssignedToI18n,
        getAssignedToText
    } from './assignedToText';

    import { lang } from "@its-ui/mono/lang/provider";
    import type { ParticipantSelectorSettings } from "./courseParticipantsDataProvider";

    export let dialogId: string = guidV4();

    export let selectedParticipantIds: number[];

    export let selectedGroupIds: number[];

    export let pageSizesList: number[];

    export let selectedGroupName: string = undefined;

    export let participantSelectorSettings: ParticipantSelectorSettings;

    export let isEnabled: boolean = true;

    export let isButtonVisible: boolean = true;

    export let isLoading: boolean = false;

    const dispatch = createEventDispatcher();

    const i18n: AssignedToI18n = {
        allParticipants: lang.main('Calendar_All_participants'),
        oneParticipant: lang.main('Task_Assigned_to_1_participant'),
        manyParticipants: (count) => lang.main('Task_Assigned_to_X_participants', count),
        groups: (count) => lang.main('Task_Assigned_to_X_groups', count),
        oneGroup: (groupName) => lang.main('Task_Assigned_to_X', groupName),
        oneParticipantAndOneGroup: (groupName) => lang.main('Task_Assigned_to_1_participant_Y', groupName),
        oneParticipantAndManyGroups: (groupCount) => lang.main('Task_Assigned_to_1_participant_Y_groups', groupCount),
        manyParticipantsAndOneGroup: (participantCount, groupName) => lang.main('Task_Assigned_to_X_participants_Y', participantCount, groupName),
        manyParticipantsAndManyGroups: (participantCount, groupCount) => lang.main('Task_Assigned_to_X_participants_Y_groups', participantCount, groupCount),
    };

    let showModal = false;

    $: participantCount = selectedParticipantIds?.length ?? 0;
    $: groupCount = selectedGroupIds?.length ?? 0;
    $: groupName = selectedGroupIds?.length === 1 ? selectedGroupName : '';
    $: valueText = getAssignedToText(participantCount, groupCount, groupName, i18n);

    function handleChangeButtonClick(): void {
        showModal = true;
    }

    function handleCancel(): void {
        showModal = false;
    }

    function handleConfirmed(event: CustomEvent<{participantIds: number[]; groupsIds: number[]; groupName: string}>): void {
        dispatch('confirmed', event.detail);

        const {
            participantIds,
            groupsIds,
            groupName
        } = event.detail;

        selectedParticipantIds = participantIds;
        selectedGroupIds = groupsIds;
        selectedGroupName = groupName;
    }
</script>
